import { applyFreeMove } from '../model/free-replay';
/**
 * Module 83 (WP-83.1) — a SHORT, not necessarily shortest, solution for a
 * board, found at generation and stored with the level.
 *
 * Moved here from the Studio (`apps/player-app/src/dev/blockjam/shortSolve.ts`,
 * WP-69) where it was the fallback for boards the optimal BFS cannot close. It
 * is now a RUNTIME solver with two jobs: the move budget's witness (a budget is
 * never below a solution the game itself has in hand — `moveBudgetFor`) and the
 * Reveal page's walkthrough (Increment B), which replays exactly these moves.
 *
 * Why best-first rather than `solveFree` or `clearableFree`: `solveFree` is
 * breadth-first and must exhaust every shallower layer before it can answer, so
 * an 11-block Expert board runs it out of memory; `clearableFree` is depth-first
 * and finds a clearing ~600 moves deep on the same board — true, and useless to
 * show anyone. Weighted best-first sits between them: priority = moves so far +
 * `weight` × blocks left, which heads for exits while preferring short paths.
 * Measured on six boards (levels 10–100): at weight 2 it matched the optimum
 * wherever BFS found one, and found 18–27 moves where BFS gave up at 12–16.
 * Runs at weight 5 first (fast, always finishes), then tightens to 3 and 2
 * while the budget allows, keeping the shortest.
 *
 * Measured over puzzles 1–40 of all three tracks at `maxNodes` 60 000 (the
 * shipped default): 120/120 solved and replay-verified, 0.4 s average, 5.7 s
 * worst (Expert 25) in Node. At 15 000 nodes 12 boards came back 1–5 moves
 * longer, which is why the default is not smaller.
 *
 * **Deterministic.** No randomness, no clock: the same board and the same
 * `maxNodes` give the same moves on every device, every time. The Reveal
 * promises "the moves allowed are more than the moves shown", and that is only
 * a promise if the stored solution can never change under the player.
 *
 * Every transition is the canonical `applyFreeMove` (Module 75: power judged
 * where the mover stands, plates passed through), not a copy of the rules, so
 * a stored solution is a sequence the board itself accepts. It is searched from
 * the STARTING layout, where neither Rainbow nor Surge is active, so no power-up
 * option is taken.
 */
const DIRECTIONS = ['up', 'down', 'left', 'right'];
const WEIGHTS = [5, 3, 2];
export const SHORT_SOLVE_DEFAULT_NODES = 60000;
export function solveRequestFor(state, maxNodes) {
    return {
        blocks: state.blocks,
        exits: state.puzzle.exits,
        gridSize: state.puzzle.gridSize,
        plates: state.puzzle.plates ?? [],
        maxNodes,
    };
}
/** `shortSolve` over a `SolveRequest` — what the worker calls. */
export function solveRequest(request) {
    return shortSolve(request.blocks, request.exits, request.gridSize, {
        maxNodes: request.maxNodes,
        plates: request.plates,
    }).moves;
}
export function shortSolve(blocks, exits, gridSize, options = {}) {
    const maxNodes = options.maxNodes ?? SHORT_SOLVE_DEFAULT_NODES;
    const plates = options.plates ?? [];
    let best = null;
    let nodes = 0;
    for (const weight of WEIGHTS) {
        const budget = maxNodes - nodes;
        if (budget <= 0)
            break;
        const r = bestFirst(blocks, exits, gridSize, plates, weight, budget);
        nodes += r.nodes;
        if (r.moves && (!best || r.moves.length < best.length))
            best = r.moves;
    }
    return { moves: best, nodes };
}
function bestFirst(start, exits, gridSize, plates, weight, maxNodes) {
    const keyOf = (bs) => bs
        .map((b) => `${b.id}@${b.position.row},${b.position.col}`)
        .sort()
        .join(';');
    const nodes = [{ blocks: start, g: 0, parent: -1, move: null }];
    const heap = new MinHeap();
    const bestG = new Map([[keyOf(start), 0]]);
    heap.push(weight * start.length, 0);
    let expanded = 0;
    while (heap.size > 0 && expanded < maxNodes) {
        const at = heap.pop();
        const node = nodes[at];
        expanded++;
        for (const block of node.blocks) {
            for (const direction of DIRECTIONS) {
                const move = { blockId: block.id, direction };
                const result = applyFreeMove(node.blocks, move, exits, gridSize, {}, plates);
                if (!result)
                    continue;
                const g = node.g + 1;
                const key = keyOf(result.blocks);
                if ((bestG.get(key) ?? Infinity) <= g)
                    continue;
                bestG.set(key, g);
                nodes.push({ blocks: result.blocks, g, parent: at, move });
                const index = nodes.length - 1;
                if (result.blocks.length === 0) {
                    const moves = [];
                    for (let i = index; i > 0; i = nodes[i].parent)
                        moves.push(nodes[i].move);
                    return { moves: moves.reverse(), nodes: expanded };
                }
                heap.push(g + weight * result.blocks.length, index);
            }
        }
    }
    return { moves: null, nodes: expanded };
}
/** Binary min-heap of `[priority, value]`; ties keep insertion order by index. */
class MinHeap {
    constructor() {
        this.items = [];
    }
    get size() {
        return this.items.length;
    }
    push(priority, value) {
        const items = this.items;
        items.push([priority, value]);
        let c = items.length - 1;
        while (c > 0) {
            const p = (c - 1) >> 1;
            if (items[p][0] <= items[c][0])
                break;
            [items[p], items[c]] = [items[c], items[p]];
            c = p;
        }
    }
    pop() {
        const items = this.items;
        const top = items[0];
        const last = items.pop();
        if (items.length > 0) {
            items[0] = last;
            let c = 0;
            for (;;) {
                const l = 2 * c + 1;
                const r = l + 1;
                let m = c;
                if (l < items.length && items[l][0] < items[m][0])
                    m = l;
                if (r < items.length && items[r][0] < items[m][0])
                    m = r;
                if (m === c)
                    break;
                [items[m], items[c]] = [items[c], items[m]];
                c = m;
            }
        }
        return top[1];
    }
}
